"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusModelLoader = void 0;
const nexus_model_1 = require("./nexus-model");
const meco_worker_wrapper_1 = require("../meshcoder/meco-worker-wrapper");
const adapters_1 = require("../adapters");
const objects_1 = require("../objects");
class NexusModelLoader {
    static get mecoWorker() {
        if (!NexusModelLoader._mecoWorker) {
            NexusModelLoader._mecoWorker = new meco_worker_wrapper_1.MecoWorkerWrapper((context, model, node, buffer) => NexusModelLoader.readyNode(context, model, node, buffer));
        }
        return NexusModelLoader._mecoWorker;
    }
    static loadModel(url, context) {
        return NexusModelLoader.fetchHeader(url, context.tokenProvider)
            .then(header => this.fetchIndex(url, header, context.tokenProvider));
    }
    static fetchHeader(url, tokenProvider) {
        return new adapters_1.NxsSingleFileModelAdapter(url, tokenProvider).fetchHeader();
    }
    static fetchIndex(url, header, tokenProvider) {
        if (!header) {
            return new Promise((resolve) => resolve(null));
        }
        return new adapters_1.NxsSingleFileModelAdapter(url, tokenProvider).fetchIndex(header)
            .then(nodes => new nexus_model_1.NexusModel(url, header, nodes));
    }
    static removeNode(context, model, node) {
        node.mesh.status = objects_1.NexusNodeDataStatus.ABSENT;
        context.state.cacheSize -= node.size;
        context.state.nodesLoaded -= 1;
        context.gl.deleteBuffer(node.mesh.verticesGlBuffer);
        context.gl.deleteBuffer(node.mesh.indicesGlBuffer);
        node.mesh.verticesGlBuffer = null;
        node.mesh.indicesGlBuffer = null;
        if (!model.vertex.texCoord) {
            return;
        }
        node.texture.references--;
        if (node.texture.references === 0 && node.texture.webGLId) {
            node.texture.status = objects_1.NexusNodeDataStatus.ABSENT;
            context.gl.deleteTexture(node.texture.webGLId);
            node.texture.webGLId = null;
        }
    }
    static requestNode(context, model, node) {
        node.mesh.status = objects_1.NexusNodeDataStatus.PENDING;
        context.state.nodesPending++;
        context.state.cacheSize += node.size;
        context.state.nodesLoaded += 1;
        const adapter = new adapters_1.NxsSingleFileModelAdapter(model.url, context.tokenProvider);
        adapter.fetchNode(model.getNodeRange(node.id))
            .then(buffer => {
            context.state.transferred += buffer.byteLength;
            return NexusModelLoader.loadNode(buffer, context, model, node);
        })
            .catch(reason => {
            if (reason.aborted) {
                console.log("Abort!");
                node.mesh.status = objects_1.NexusNodeDataStatus.ABSENT;
            }
            else {
                console.log("Failed loading node: " + node.id);
            }
            context.state.nodesPending--;
        });
        if (!model.hasTextures) {
            return;
        }
        node.texture.references++;
        if (node.texture.webGLId) {
            // texture has already been loaded
            node.texture.status = objects_1.NexusNodeDataStatus.PRESENT;
            return;
        }
        node.texture.status = objects_1.NexusNodeDataStatus.PENDING;
        adapter.fetchNodeTexture(node.texture)
            .then(buffer => {
            context.state.transferred += buffer.size;
            return NexusModelLoader.loadTexture(buffer, context, node);
        })
            .catch(reason => {
            if (reason.aborted) {
                console.log("Abort!");
                node.texture.status = objects_1.NexusNodeDataStatus.ABSENT;
                node.texture.webGLId = null;
            }
            else {
                console.log("Failed loading node texture: " + node.id);
            }
        });
    }
    static loadNode(buffer, context, model, node) {
        if (!model.compressed) {
            NexusModelLoader.readyNode(context, model, node, buffer);
        }
        else {
            const lastTriangles = node.patches.map((patch) => patch.lastTriangle);
            // TODO: change so we add a callback right here -> easier to see that ready node is called the same way
            // 	no need to pass the context, node and so on to the worker...
            NexusModelLoader.mecoWorker.postRequest(context, model, node, lastTriangles, buffer);
        }
    }
    static loadTexture(buffer, context, node) {
        if (node.mesh.status === objects_1.NexusNodeDataStatus.ABSENT) {
            node.texture.status = objects_1.NexusNodeDataStatus.ABSENT;
            return; //aborted
        }
        const urlCreator = window.URL || window.webkitURL;
        const img = document.createElement('img');
        img.onerror = function (e) { console.log("Failed loading texture.", e); };
        img.src = urlCreator.createObjectURL(buffer);
        const gl = context.gl;
        img.onload = function () {
            urlCreator.revokeObjectURL(img.src);
            const flip = gl.getParameter(gl.UNPACK_FLIP_Y_WEBGL);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
            node.texture.webGLId = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, node.texture.webGLId);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flip);
            node.texture.status = objects_1.NexusNodeDataStatus.PRESENT;
            context.modelStateChanged();
        };
    }
    static readyNode(context, model, node, buffer) {
        const gl = context.gl;
        node.verticesBuffer = new Float32Array(buffer, 0, node.vertices * 3);
        node.mesh.verticesGlBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, node.mesh.verticesGlBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Uint8Array(buffer, 0, node.vertices * model.vertexSize), gl.STATIC_DRAW);
        node.indicesBuffer = new Uint16Array(buffer, node.vertices * model.vertexSize, node.faces * 3);
        node.mesh.indicesGlBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, node.mesh.indicesGlBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint8Array(buffer, node.vertices * model.vertexSize, node.faces * model.faceSize), gl.STATIC_DRAW);
        node.mesh.status = objects_1.NexusNodeDataStatus.PRESENT;
        context.state.nodesPending--;
        if (node.status === objects_1.NexusNodeDataStatus.PRESENT) {
            context.modelStateChanged();
        }
    }
    static updateCache(context, forceRemoveWorstNodes = false) {
        // When the cache changes, the change is relatively big, in that case, clean worst nodes first.
        if (forceRemoveWorstNodes) {
            NexusModelLoader.removeWorstNodes(context);
        }
        let best = this.findBestCandidate(context);
        if (!best) {
            return;
        }
        const anyWorseNodeToRemove = NexusModelLoader.removeWorstNodes(context, best);
        if (anyWorseNodeToRemove) {
            NexusModelLoader.requestNode(context, best.model, best.node);
        }
    }
    static findBestCandidate(context) {
        let best = null;
        for (const candidate of context.candidates) {
            const isNodeAbsent = candidate.node.status === objects_1.NexusNodeDataStatus.ABSENT;
            if (isNodeAbsent && (!best || candidate.error > best.error)) {
                best = candidate;
            }
        }
        context.candidates = [];
        return best;
    }
    static removeWorstNodes(context, limit) {
        while (context.state.cacheSize > context.settings.maxCacheSize) {
            let worst = this.findWorstNode(context);
            if (!worst || (limit && worst.error >= limit.error && worst.frame == limit.frame)) {
                return false;
            }
            NexusModelLoader.removeNode(context, worst.model, worst.node);
        }
        return true;
    }
    static findWorstNode(context) {
        let worst = null;
        //find worst in cache
        for (const model of context.models) {
            for (let node of model.nodes) {
                const isNodeLoaded = node.status === objects_1.NexusNodeDataStatus.PRESENT;
                if (!worst || (isNodeLoaded && node.renderState.error < worst.error)) {
                    worst = {
                        context: context,
                        model: model,
                        node: node,
                        error: node.renderState.error,
                        frame: node.renderState.frame
                    };
                }
            }
        }
        return worst;
    }
}
exports.NexusModelLoader = NexusModelLoader;
